import {
  COMPANY_SIGN_IN_CONSENT_GRANTED,
  COMPANY_SIGN_IN_SETTINGS_PATH,
  completeMicrosoftConsentSchema,
} from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import { Button, Card, CardDescription, CardFooter, CardHeader } from '@instanexus/ui';
import { setAccessToken } from '@instanexus/web-auth';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { useApiClient } from '@/hooks/use-api-client';
import { centralLoginPath } from '@/lib/auth-paths';
import { companySignInConsentSettingsUrl } from '@/lib/company-sign-in-test';
import { clientAppOrigin } from '@/lib/sign-in-request';

/** Not a declared `ErrorCode`; `errorMessage()` falls back to `errors:unexpected`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';
const STATE_INVALID = 'FEDERATED_STATE_INVALID';

/** Company sign-in settings live in platform-admin, so the approval always belongs to the admin client. */
const CLIENT = 'admin';

type Phase = 'working' | 'signed-out' | 'error';

function redirectTo(url: string): void {
  window.location.assign(url);
}

/**
 * `/company-sign-in/consent` — the central redirect URI of the Microsoft admin-consent round trip
 * (ADR 0042 decision 19). Without `state` it starts the approval; with `state` it finishes it.
 */
export function CompanySignInConsentPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('working');
  const [errorCode, setErrorCode] = useState<string>(UNEXPECTED_FAILURE);

  const isFinish = searchParams.get('state') !== null;
  const origin = clientAppOrigin(CLIENT);
  const backUrl =
    origin === null ? null : new URL(COMPANY_SIGN_IN_SETTINGS_PATH, origin).toString();

  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const fail = (code: string): void => {
      setErrorCode(code);
      setPhase('error');
    };
    const leaveWith = (result: string): void => {
      const url = companySignInConsentSettingsUrl(CLIENT, result);
      if (url === null) {
        fail(result === COMPANY_SIGN_IN_CONSENT_GRANTED ? UNEXPECTED_FAILURE : result);
        return;
      }
      redirectTo(url);
    };

    const state = searchParams.get('state');
    void (async (): Promise<void> => {
      try {
        const session = await api.auth.refreshSession();
        if (session === undefined) {
          setPhase('signed-out');
          return;
        }
        setAccessToken(session.accessToken);

        if (state === null) {
          const { consentUrl } = await api.sso.startMicrosoftConsent();
          redirectTo(consentUrl);
          return;
        }

        const body = completeMicrosoftConsentSchema.safeParse(
          searchParams.has('error')
            ? { state, error: searchParams.get('error') }
            : { state, tenant: searchParams.get('tenant') },
        );
        if (!body.success) {
          leaveWith(STATE_INVALID);
          return;
        }
        try {
          await api.sso.completeMicrosoftConsent(body.data);
          leaveWith(COMPANY_SIGN_IN_CONSENT_GRANTED);
        } catch (error: unknown) {
          leaveWith(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
        }
      } catch (error: unknown) {
        fail(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
      }
    })();
  }, [api, searchParams]);

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-3xl font-semibold text-primary text-balance">
            {t('companySignInConsent.title')}
          </h1>
          {phase === 'working' ? (
            <p role="status" className="text-sm text-muted-foreground">
              {t(isFinish ? 'companySignInConsent.finishing' : 'companySignInConsent.starting')}
            </p>
          ) : null}
          {phase === 'signed-out' ? (
            <CardDescription>{t('companySignInConsent.signedOutBody')}</CardDescription>
          ) : null}
          {phase === 'error' ? (
            <CardDescription role="alert">{errorMessage(t, errorCode)}</CardDescription>
          ) : null}
        </CardHeader>
        {phase === 'signed-out' ? (
          <CardFooter>
            <Button asChild>
              <Link to={centralLoginPath(CLIENT)}>{t('companySignInConsent.signIn')}</Link>
            </Button>
          </CardFooter>
        ) : null}
        {phase === 'error' && backUrl !== null ? (
          <CardFooter>
            <Button asChild variant="outline">
              <a href={backUrl}>{t('companySignInConsent.back')}</a>
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </AuthCardLayout>
  );
}
